import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart3, ShieldCheck, AlertTriangle, LineChart as LineChartIcon } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { formatCurrency, formatPercent } from '../lib/format';

// Palette di colori distinti (tinte ricche, non colori puri) per le linee del grafico
const PALETTE = [
  '#2563eb', '#0ea5e9', '#14b8a6', '#059669', '#65a30d', '#ca8a04', '#ea580c',
  '#dc2626', '#e11d48', '#db2777', '#9333ea', '#7c3aed', '#4f46e5', '#0891b2',
  '#16a34a', '#b45309', '#475569',
];

export default function KpiConti() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set(['ebitda', 'ebit', 'utile_netto']));

  useEffect(() => {
    api.getFierKpiConti()
      .then(setData)
      .catch(() => toast.error('Errore nel caricamento dei KPI'))
      .finally(() => setLoading(false));
  }, []);

  const balances = data?.balances || [];
  const groups = data?.groups || [];
  const allItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const colorFor = useMemo(() => {
    const m = {};
    allItems.forEach((it, i) => { m[it.id] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [allItems]);

  const chartData = useMemo(() => balances.map((b) => {
    const row = { name: b.label };
    allItems.forEach((it) => {
      const pct = b.kpi?.[it.id]?.pct;
      row[it.id] = pct != null ? +(pct * 100).toFixed(2) : null;
    });
    return row;
  }), [balances, allItems]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectGroup = (g, on) => setSelected((prev) => {
    const n = new Set(prev);
    g.items.forEach((it) => { if (on) n.add(it.id); else n.delete(it.id); });
    return n;
  });

  const cell = (b, it) => {
    const v = b.kpi?.[it.id];
    if (!v) return <span className="text-muted-foreground">—</span>;
    return (
      <div className="flex flex-col items-end leading-tight">
        <span className="tabular-nums font-medium">{formatPercent(v.pct, 1, true)}</span>
        {it.mode === 'eur_mese_pct' && (
          <span className="text-[10px] text-muted-foreground tabular-nums">{formatCurrency(v.eur_mese)}/mese</span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="kpi-conti-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><BarChart3 className="h-5 w-5 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">KPI conti</h1>
          <p className="text-sm text-muted-foreground">Indicatori chiave di Fier S.p.A. comparati tra bilancio annuale ufficiale e bilanci di periodo · % sui ricavi</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-72 w-full rounded-xl" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      ) : (
        <>
          {/* GRAFICO */}
          <Card className="p-0 overflow-hidden" data-testid="kpi-chart-card">
            <div className="px-5 py-3 border-b flex items-center gap-2">
              <LineChartIcon className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Andamento KPI (% sui ricavi)</h3>
              <span className="ml-auto text-[11px] text-muted-foreground">{selected.size} selezionati</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
              <div className="p-4 min-h-[320px]">
                {selected.size === 0 ? (
                  <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">Seleziona almeno un KPI dai flag a destra</div>
                ) : (
                  <ResponsiveContainer width="100%" height={340}>
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        formatter={(value, name) => [`${value}%`, allItems.find((i) => i.id === name)?.label || name]}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                      />
                      <Legend formatter={(value) => <span style={{ fontSize: 11 }}>{allItems.find((i) => i.id === value)?.label || value}</span>} />
                      {allItems.filter((it) => selected.has(it.id)).map((it) => (
                        <Line key={it.id} type="monotone" dataKey={it.id} stroke={colorFor[it.id]}
                          strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              {/* Pannello flag KPI */}
              <div className="border-t lg:border-t-0 lg:border-l p-4 space-y-4 max-h-[420px] overflow-y-auto" data-testid="kpi-flags">
                {groups.map((g) => {
                  const allOn = g.items.every((it) => selected.has(it.id));
                  return (
                    <div key={g.group} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.group}</span>
                        <button className="text-[10px] text-primary hover:underline" onClick={() => selectGroup(g, !allOn)} data-testid={`kpi-group-toggle-${g.group}`}>
                          {allOn ? 'Deseleziona' : 'Tutti'}
                        </button>
                      </div>
                      {g.items.map((it) => (
                        <label key={it.id} className="flex items-center gap-2 cursor-pointer py-0.5" data-testid={`kpi-flag-${it.id}`}>
                          <Checkbox checked={selected.has(it.id)} onCheckedChange={() => toggle(it.id)} />
                          <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ background: colorFor[it.id] }} />
                          <span className="text-[13px]">{it.label}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* TABELLA */}
          <Card className="p-0 overflow-hidden" data-testid="kpi-table-card">
            <div className="px-5 py-3 border-b">
              <h3 className="text-sm font-semibold">Tabella comparativa KPI</h3>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="min-w-[180px] sticky left-0 bg-card z-20">KPI</TableHead>
                    {balances.map((b) => (
                      <TableHead key={b.id} className="text-right min-w-[130px]">
                        <div className="flex flex-col items-end gap-1">
                          <span className="font-semibold text-foreground">{b.label}</span>
                          {b.type === 'annuale'
                            ? <Badge variant="outline" className="text-[9px] text-[hsl(var(--success))] border-[hsl(var(--success))]/40 gap-1"><ShieldCheck className="h-2.5 w-2.5" />Ufficiale</Badge>
                            : (b.pronto
                                ? <Badge variant="outline" className="text-[9px] text-[hsl(var(--success))] border-[hsl(var(--success))]/40">Verificato · {b.n_mesi}m</Badge>
                                : <Badge variant="outline" className="text-[9px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 gap-1"><AlertTriangle className="h-2.5 w-2.5" />da verificare</Badge>)}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => (
                    <React.Fragment key={g.group}>
                      <TableRow className="bg-muted/40">
                        <TableCell colSpan={balances.length + 1} className="py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sticky left-0">{g.group}</TableCell>
                      </TableRow>
                      {g.items.map((it) => (
                        <TableRow key={it.id} data-testid={`kpi-row-${it.id}`}>
                          <TableCell className="sticky left-0 bg-card z-10">
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ background: colorFor[it.id] }} />
                              <span className="text-sm">{it.label}</span>
                            </span>
                          </TableCell>
                          {balances.map((b) => (
                            <TableCell key={b.id} className="text-right">{cell(b, it)}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
